import React, {
  Fragment, useState, useEffect, useRef,
} from 'react'
import { string } from 'prop-types'

import { Checkbox } from '@cgtrader/triangle-ui/dist/src/ssr-index'

import InputField from 'Pods/Shared/NewInputField'
import { run, RuleRunner } from 'Pods/Shared/RuleRunner'
import { required } from 'Pods/Shared/Rules/Rules'
import Icon from 'Pods/Shared/Icon/Icon'
import MTCaptcha from 'Utils/MTCaptcha'
import useSentry from 'Pods/Shared/Hooks/useSentry'

const fieldValidations = [
  RuleRunner('email', 'Email', required),
  RuleRunner('password', 'Password', required),
]

function Login({ customId = '', captchaId = '' }) {
  const [fields, setFields] = useState({
    email: '',
    password: '',
    remember: true,
    twoFASecondState: false,
    twoFaCode: '',
    showErrors: false,
    validationErrors: {},
  })
  const loginCaptchaEnabled = window.gon.features && window.gon.features.lcaptcha
  const { captureException } = useSentry()

  useEffect(() => {
    setFields({ ...fields, validationErrors: run(fields, fieldValidations) })
  }, [])

  function errorFor(field) {
    return fields.validationErrors[field] || ''
  }

  function handleInputChange(e) {
    const newFields = { ...fields, [e.target.name]: e.target.value }
    setFields({ ...newFields, validationErrors: run(newFields, fieldValidations) })
  }

  function handleSocialLogin(e) {
    e.preventDefault()
    e.target.submit()
  }

  function handleSubmit(e) {
    e.preventDefault()

    try {
      setFields({ ...fields, showErrors: true })

      if (Object.getOwnPropertyNames(fields.validationErrors).length !== 0) {
        return null
      }

      axios.post('/users/2fa-or-login', {
        user: {
          login: fields.email,
          email_confirmation: fields.email_confirmation, // honey pot field
          password: fields.password,
          remember_me: fields.remember,
          ...(loginCaptchaEnabled && { MTCaptchaToken: window.mtcaptcha.getVerifiedToken(captchaId) }),
        },
        location: window.location.pathname,
      })
        .then(response => onSubmitSuccess(response))
        .catch(response => onSubmitFailure(response))
    } catch (error) {
      captureException(error, {
        tags: { section: 'React login' },
        extra: {
          msg: 'Login error',
          email: fields.email,
        },
      })
    }
  }

  function onSubmitSuccess(response) {
    if (response.data.twoFaEnabled) {
      setFields({ ...fields, twoFASecondState: true })
    } else if (typeof response.data === 'string') {
      // Security tool asks for verification.
      document.write(response.data)
    } else {
      App.events.publish('app:notification:flash:success', I18n.t('devise.sessions.signed_in'))

      axios.put('/cg/checkout/track-login-to-payment-step', { email: fields.email }).catch((error) => { })

      if (response.data.redirectTo) {
        window.location = response.data.redirectTo
      } else {
        window.location.reload()
      }
    }
  }

  function onSubmitFailure(xhr) {
    if (xhr.response?.status !== 401 && xhr.response?.status !== 200) {
      captureException(new Error('Login submission failed'), {
        tags: { section: 'React login' },
        extra: {
          msg: 'Login submission failure',
          email: fields.email,
          xhrResponse: xhr.response?.data,
          status: xhr.response?.status,
        },
      })
    }

    if (loginCaptchaEnabled) {
      window.mtcaptcha.resetUI(captchaId)
    }
    App.events.publish('app:notification:flash:error', xhr.response.data.errors)
  }

  function handleTwoFaSubmit(e) {
    e.preventDefault()

    if (fields.twoFaCode === '') {
      App.events.publish('app:notification:flash:error', 'Please enter code')
      return
    }

    axios.post('/users/2fa-or-login', {
      user: {
        login: fields.email,
        password: fields.password,
        remember_me: fields.remember,
      },
      code: fields.twoFaCode,
      location: window.location.pathname,
    })
      .then(response => onSubmitSuccess(response))
      .catch(response => onSubmitFailure(response))
  }

  function toggleChecked() {
    setFields({ ...fields, remember: !fields.remember })
  }

  const token = document.querySelector('meta[name="csrf-token"]')?.content
  return (
    <Fragment>
      {!fields.twoFASecondState && (
        <div className="user-auth-container">
          <div className="row">
            <div className="col-12">
              <form onSubmit={handleSubmit}>
                <InputField
                  label="Email or Username"
                  id={`email${customId}`}
                  name="email"
                  type="text"
                  errorText={errorFor('email')}
                  showError={fields.showErrors}
                  value={fields.email}
                  onChange={handleInputChange}
                />
                {/* honey pot field */}
                <div className="cgt-input hidden">
                  <InputField
                    label="Email Confirmation"
                    id={`email_confirmation${customId}`}
                    name="email_confirmation"
                    type="text"
                    errorText={errorFor('email_confirmation')}
                    showError={fields.showErrors}
                    value={fields.email_confirmation}
                    onChange={handleInputChange}
                    autoComplete="off"
                  />
                </div>
                <InputField
                  label="Password"
                  id={`password${customId}`}
                  name="password"
                  type="password"
                  errorText={errorFor('password')}
                  showError={fields.showErrors}
                  value={fields.password}
                  onChange={handleInputChange}
                />
                <div className="row">
                  <div className="col">
                    <Checkbox
                      checkedColor="primary"
                      id={`remember-me${customId}`}
                      label="Remember me"
                      isChecked={fields.remember}
                      onChange={toggleChecked}
                    />
                  </div>
                  <div className="col cgt-legacy-text--align-right">
                    <a href="/users/password/new" className="cgt-legacy-link">Forgot password?</a>
                  </div>
                </div>
                {loginCaptchaEnabled && <MTCaptcha captchaId={captchaId} />}
                <div className="row">
                  <div className="col-12">
                    <button
                      className="cgt-button cgt-button--primary cgt-button--width-expand justify-content-center"
                      type="submit"
                    >
                      Log in
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
          <div className="row">
            <div className="col-12" />
          </div>
          <div className="row">
            <div className="col-12">
              <form action="/users/auth/google_oauth2" method="post" onSubmit={handleSocialLogin}>
                <button
                  type="submit"
                  className="cgt-button cgt-button--width-expand cgt-button--social cgt-button--google-plus justify-content-center"
                >
                  Continue via Google
                </button>
                {token && <input type="hidden" name="authenticity_token" value={token} />}
              </form>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <form action="/users/auth/facebook" method="post" onSubmit={handleSocialLogin}>
                <button
                  type="submit"
                  className="cgt-button cgt-button--width-expand cgt-button--social cgt-button--facebook justify-content-center"
                >
                  Continue via Facebook
                </button>
                {token && <input type="hidden" name="authenticity_token" value={token} />}
              </form>
            </div>
          </div>
        </div>
      )}
      {fields.twoFASecondState && (
        <div className="user-auth-container">
          <p className="heading-20 u-text-center">Enter your login verification code</p>
          <p className="u-text-center">Use the verification code sent to your email (or from your Authenticator app) to log in to your CGTrader account</p>
          <form onSubmit={handleTwoFaSubmit}>
            <InputField
              label="Please enter the 6-digit code. Code contains only numbers"
              name="twoFaCode"
              id="twoFaCode"
              type="text"
              required
              value={fields.twoFaCode}
              onChange={handleInputChange}
            />
            <button
              className="cgt-button cgt-button--primary cgt-button--width-expand"
              type="submit"
            >
              Verify
            </button>
          </form>
        </div>
      )}
    </Fragment>
  )
}

Login.propTypes = {
  customId: string,
  captchaId: string,
}

export default Login
